/* ME → BRAND (her call, 2026-10-12): her colours as COLOURS, picked and kept,
 * so every tool wears the same ones. Until now each reader (decks, the site
 * brief, the designed email, Social's picture brief) worked them out from the
 * prose the Accelerator locked — five palettes offered, the chosen one
 * inferred, and one account had no colour codes at all.
 *
 * TWO LISTS (her design, same day, after a round of chips that hid a rule):
 *   · YOUR COLOURS — what she has: a name, the colour, and the colour that
 *     goes ON it (its text), with the contrast between them;
 *   · WHERE THEY GO — a short fixed list of JOBS, each pointing at one of
 *     her colours. A colour can do many jobs (Salmon: second button AND
 *     highlights); a job has exactly one colour, so no tool ever has to guess
 *     which teal is "the" button.
 *
 * ME WINS (her call): where Me fills a job, every tool uses it. The locked
 * Accelerator palette is read only for a job Me leaves empty. When the two
 * DISAGREE nothing changes by itself — the conflict is NAMED
 * (`brandConflict`) and she chooses.
 *
 * ONE READER for every tool: `brandSkin`. Stored in settings.brand (0037):
 *   { palette: [{ id, name, hex: "RRGGBB", on: "RRGGBB" | null }],
 *     jobs: { headings: id, links: id, button: id, … } }
 */

import { paletteEntries, readBrandSkin, readFonts } from "./brandSkin";
import { contrast, luminance } from "./deckLayout";

/* THE JOBS, in her words. `skin` is the role the tools already speak (decks,
   siteTokens, the email); `against` is what it sits on, for the contrast
   shown beside the job; `min` the ratio it needs. */
export const BRAND_JOBS = [
  { id: "headings", label: "Headings", hint: "Titles and section headings", skin: "brandPrimary", against: "lightBg", min: 3 },
  { id: "links", label: "Links", hint: "Words people can click", skin: "link", against: "lightBg", min: 4.5 },
  { id: "button", label: "Main button", hint: "The one thing you want pressed", skin: "brandAccent", against: "own", min: 4.5 },
  { id: "button2", label: "Second button", hint: "A quieter second choice", skin: "button2", against: "own", min: 4.5 },
  { id: "highlight", label: "Highlights", hint: "Underlines, tags, a word that should pop", skin: "highlight", against: null, min: 0 },
  /* ACCENT (her ask, 2026-10-12): the supporting color — not a button, not a
     word to read, so nothing to be read against. */
  { id: "accent", label: "Accent", hint: "Supporting color — borders, icons, shapes", skin: "accent", against: null, min: 0 },
  { id: "darkBg", label: "Dark background", hint: "Deep sections, title slides, the footer", skin: "bgDark", against: null, min: 0 },
  { id: "lightBg", label: "Light background", hint: "The page itself", skin: "bgPrimary", against: null, min: 0 },
  { id: "text", label: "Text", hint: "Body words on the light background", skin: "textOnLight", against: "lightBg", min: 4.5 },
  { id: "textOnDark", label: "Text on dark", hint: "Body words on the dark background", skin: "textOnDark", against: "darkBg", min: 4.5 },
];
export const MAX_COLOURS = 12;

/* ROLES (her design, 2026-10-12, third pass): the main view asks the question
   people actually know the answer to — "my primary is teal" — and the JOBS
   above are WORKED OUT from it (`effectiveJobs`). Fine-tune, behind a toggle,
   overrides any job; a job she never touched follows her roles, so changing
   a role moves everything built on it. NEUTRAL is hers too: navy, cream and
   charcoal are what everything sits on, not brand colors pretending to be
   Tertiary. */
export const BRAND_ROLES = [
  { id: "primary", label: "Primary" },
  { id: "secondary", label: "Secondary" },
  { id: "tertiary", label: "Tertiary" },
  { id: "accent", label: "Accent" },
  /* BACKGROUND and TEXT (her ask, 2026-10-12: "add background to the
     dropdown then we can do the soft white and dark") replaced Neutral in the
     list. A color already saved as Neutral still works — it can be either. */
  { id: "background", label: "Background" },
  { id: "text", label: "Text" },
];
const ROLE_IDS = new Set([...BRAND_ROLES.map((r) => r.id), "neutral"]);

/** "#0aefd3", "0AEFD3", " #0AE " → "0AEFD3" (the stored form), else "". */
export function normHex(v) {
  const h = String(v || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{6}$/i.test(h)) return h.toUpperCase();
  if (/^[0-9a-f]{3}$/i.test(h)) return h.split("").map((c) => c + c).join("").toUpperCase();
  return "";
}

let seq = 0;
export const newColourId = () => `c${Date.now().toString(36)}${(seq++).toString(36)}`;

/**
 * What is stored, made safe to read. Junk colours are dropped, and a job that
 * points at a colour no longer in the palette is simply unfilled. The FIRST
 * shape (five fixed roles, `{ colors: { main, … } }`, live for an afternoon)
 * reads as a palette too, so nothing saved then is lost.
 */
export function readBrand(stored) {
  if (stored?.colors && !stored?.palette) {
    const legacy = { main: ["Brand color", "headings"], button: ["Button color", "button"], dark: ["Dark background", "darkBg"], light: ["Light background", "lightBg"], text: ["Text", "text"] };
    const palette = [];
    const jobs = {};
    for (const [k, [name, job]] of Object.entries(legacy)) {
      const hex = normHex(stored.colors[k]);
      if (!hex) continue;
      palette.push({ id: `legacy-${k}`, name, hex, on: null, role: k === "main" ? "primary" : k === "button" ? "accent" : k === "text" ? "text" : "background" });
      jobs[job] = `legacy-${k}`;
    }
    if (jobs.headings) jobs.links = jobs.headings;
    return { palette, jobs, fonts: {}, logos: {} };
  }
  const palette = (Array.isArray(stored?.palette) ? stored.palette : [])
    .map((c) => ({ id: String(c?.id || ""), name: String(c?.name || "").trim(), hex: normHex(c?.hex), on: normHex(c?.on) || null, role: ROLE_IDS.has(c?.role) ? c.role : "" }))
    .filter((c) => c.id && c.hex)
    .slice(0, MAX_COLOURS);
  const ids = new Set(palette.map((c) => c.id));
  const jobs = {};
  for (const j of BRAND_JOBS) if (ids.has(stored?.jobs?.[j.id])) jobs[j.id] = stored.jobs[j.id];
  return { palette, jobs, fonts: readFontPair(stored?.fonts), logos: readLogos(stored?.logos) };
}

export const hasBrandColors = (brand) => readBrand(brand).palette.length > 0;

/* LOGOS (her call, 2026-10-13: "side by side dark versus light"). Three
   slots, no more — no tool would use a fourth. The LIGHT one is the logo she
   always had (settings.logo_url, 0028), so nothing she set moves; the dark
   one and the icon live here in settings.brand. Links only, http(s). */
export const LOGO_SLOTS = [
  { id: "light", label: "For light backgrounds", hint: "Dark lettering — emails, light pages, the website header" },
  { id: "dark", label: "For dark backgrounds", hint: "Light lettering — dark sections, title slides, the footer" },
  { id: "icon", label: "Icon", hint: "Small and square — a favicon, an avatar" },
];
const isLink = (v) => /^https?:\/\/\S+$/i.test(String(v || "").trim());
function readLogos(l) {
  const out = {};
  if (isLink(l?.dark)) out.dark = String(l.dark).trim();
  if (isLink(l?.icon)) out.icon = String(l.icon).trim();
  return out;
}
/** All three, the light one from where it has always lived. */
export function brandLogos(brand, logoUrl) {
  const b = readBrand(brand);
  return { ...(isLink(logoUrl) ? { light: String(logoUrl).trim() } : {}), ...b.logos };
}

/* FONTS (2026-10-12): a heading font and a body font, by their Google Fonts
   names. Me wins here too; the Accelerator's pair is offered, never filled. */
export const FONT_SLOTS = [
  { id: "heading", label: "Heading font", hint: "Titles and section headings", skin: "fontHeading" },
  { id: "body", label: "Body font", hint: "Everything people read", skin: "fontBody" },
];
function readFontPair(f) {
  const clean = (v) => String(v || "").replace(/[{}"<>;]/g, "").replace(/\s+/g, " ").trim().slice(0, 60);
  const out = {};
  if (clean(f?.heading)) out.heading = clean(f.heading);
  if (clean(f?.body)) out.body = clean(f.body);
  return out;
}

/** Her locked Typography Pairing, as Me's two slots. */
export function acceleratorFonts(voiceOutputs = {}) {
  const { heading, body } = readFonts(voiceOutputs?.["Typography Pairing"] || "");
  return readFontPair({ heading, body });
}

/** Where Me's fonts and the Accelerator's DISAGREE — named, never settled. */
export function fontConflict(brand, voiceOutputs = {}) {
  const mine = readBrand(brand).fonts;
  const theirs = acceleratorFonts(voiceOutputs);
  return FONT_SLOTS.filter((s) => mine[s.id] && theirs[s.id] && mine[s.id].toLowerCase() !== theirs[s.id].toLowerCase())
    .map((s) => ({ slot: s.id, label: s.label, mine: mine[s.id], accelerator: theirs[s.id] }));
}

/**
 * Every job's colour id: what she set in Fine-tune, else what her ROLES say
 * (her call: Accent drives the buttons). Returns { jobs, derived } — `derived`
 * names the jobs that are following her roles rather than her own pick.
 */
export function effectiveJobs(brand) {
  const b = readBrand(brand);
  const by = (role) => b.palette.filter((c) => c.role === role);
  const [primary] = by("primary"), [secondary] = by("secondary"), [tertiary] = by("tertiary");
  const accents = by("accent");
  const neutrals = by("neutral");
  const bgs = [...by("background"), ...neutrals];
  const texts = [...by("text"), ...neutrals];
  const lum = (c) => luminance(c.hex);
  /* A Background is a background whatever its lightness; a Neutral only
     when it is clearly dark or clearly light. */
  const darkest = (list) => [...list].sort((x, y) => lum(x) - lum(y))[0];
  const lightest = (list) => [...list].sort((x, y) => lum(y) - lum(x))[0];
  const darkBg = darkest(bgs.filter((c) => lum(c) < 0.2));
  const lightBg = lightest(bgs.filter((c) => lum(c) > 0.6 && c !== darkBg));
  /* Text: the neutral that reads best on her light background, but not the
     dark background itself when another one reads well enough — charcoal for
     body words, navy kept for the dark sections. */
  const readsOn = (bg, skip, pool = texts) => (bg ? pool.filter((c) => c !== bg && !skip.includes(c)) : [])
    .map((c) => [c, contrast(c.hex, bg.hex)]).filter(([, r]) => r >= 4.5).sort((x, y) => y[1] - x[1]).map(([c]) => c);
  const text = readsOn(lightBg, [darkBg])[0] || darkBg;
  /* On dark: a Text color that reads there, else any of hers that does —
     Soft White marked Background is the right words for navy. */
  const textOnDark = readsOn(darkBg, [lightBg])[0] || readsOn(darkBg, [], [...texts, ...bgs])[0] || lightBg;
  const fromRoles = {
    headings: primary || secondary || tertiary,
    links: secondary || primary,
    button: accents[0] || primary,
    button2: accents[1] || secondary,
    highlight: accents[1] || accents[0],
    accent: tertiary || accents[0],
    darkBg, lightBg, text, textOnDark,
  };
  const jobs = {};
  const derived = [];
  for (const j of BRAND_JOBS) {
    if (b.jobs[j.id]) jobs[j.id] = b.jobs[j.id];
    else if (fromRoles[j.id]) { jobs[j.id] = fromRoles[j.id].id; derived.push(j.id); }
  }
  return { jobs, derived };
}

/** The colour a job points at (her pick, else her roles'), or null. */
export function jobColour(brand, jobId) {
  const b = readBrand(brand);
  const id = effectiveJobs(brand).jobs[jobId];
  return b.palette.find((c) => c.id === id) || null;
}

/* TEXT ON IT IS AUTOMATIC (her call, 2026-10-12: "just do that
   automatically"): Soft White or Charcoal, whichever reads better on the
   color. `on` stored on a color is HER override; null means automatic. */
export const SOFT_WHITE = "F0EEEB";
export const CHARCOAL = "2C2C2C";
export function autoOn(hex) {
  const h = normHex(hex);
  if (!h) return null;
  return contrast(h, SOFT_WHITE) >= contrast(h, CHARCOAL) ? SOFT_WHITE : CHARCOAL;
}
/** The words on a color: her pick, else automatic. */
export const onOf = (c) => (c ? c.on || autoOn(c.hex) : null);

/**
 * The words that read best ON a colour, chosen from her own colours (and,
 * only when none of hers will do, black or white). What "Text on it" offers
 * before she picks.
 */
export function suggestOn(hex, palette = []) {
  const h = normHex(hex);
  if (!h) return null;
  const mine = palette.map((c) => c.hex).filter((x) => x && x !== h);
  const best = (list) => list.map((x) => [x, contrast(h, x)]).sort((a, b) => b[1] - a[1])[0];
  const fromHers = best(mine);
  if (fromHers && fromHers[1] >= 4.5) return fromHers[0];
  const plain = best(["000000", "FFFFFF"]);
  return fromHers && fromHers[1] >= plain[1] ? fromHers[0] : plain[0];
}

/** The ratio between two colours, to one decimal, or null. */
export function ratio(a, b) {
  const x = normHex(a), y = normHex(b);
  return x && y ? Math.round(contrast(x, y) * 10) / 10 : null;
}

/* The prose names a colour as "**Warm Cream**", "Primary", "Accent 1:" … —
   kept when it is a NAME, dropped when it is only the prompt's role label. */
function nameFrom(entry, i) {
  const n = String(entry.name || "").replace(/^(primary|secondary|tertiary|accent ?1|accent ?2)\b[\s:—–-]*/i, "").replace(/[*_:]/g, "").trim();
  return n || `Color ${i + 1}`;
}

/**
 * Her locked Accelerator palette as Me would hold it: every colour of the
 * FIRST palette, named from the prose, and the jobs the skin reader settled.
 * What "Start from your Accelerator palette" offers, and the other side of a
 * conflict.
 */
export function acceleratorBrand(voiceOutputs = {}) {
  const prose = voiceOutputs?.["Brand Feeling & Color Palette"] || voiceOutputs?.["Color Palette"] || "";
  const { entries, fromNames } = paletteEntries(prose);
  const { skin } = readBrandSkin({ palette: prose, typography: voiceOutputs?.["Typography Pairing"] || "" });
  const palette = entries.slice(0, MAX_COLOURS).map((e, i) => ({ id: `acc-${e.hex}`, name: nameFrom(e, i), hex: e.hex, on: null }));
  /* Text on each is AUTOMATIC (on: null) - Soft White or Charcoal. */
  /* ROLES from what the skin reader settled, then the prompt's own labels —
     its "Primary" is often the PAGE BASE, so a colour read as a background or
     text is Neutral whatever it was labelled. */
  const as = (role) => normHex(skin[role]);
  for (const [c, e] of palette.map((c, i) => [c, entries[i]])) {
    if ([as("bgDark"), as("bgPrimary")].includes(c.hex)) c.role = "background";
    else if ([as("textOnLight"), as("textOnDark")].includes(c.hex)) c.role = "text";
    else if (as("brandAccent") === c.hex) c.role = "accent";
    else if (as("brandPrimary") === c.hex) c.role = "primary";
    else if (e.label === "secondary") c.role = "secondary";
    else if (e.label === "tertiary") c.role = "tertiary";
    else if (e.label === "accent2" || e.label === "accent1") c.role = luminance(c.hex) > 0.25 && luminance(c.hex) < 0.6 && !palette.some((x) => x.role === "tertiary") ? "tertiary" : "accent";
    else c.role = luminance(c.hex) > 0.75 || luminance(c.hex) < 0.03 ? "background" : "secondary";
  }
  /* The jobs the skin reader settled are kept as her starting picks, so
     "Use these" reproduces exactly what the tools wore before. */
  const jobs = {};
  for (const j of BRAND_JOBS) {
    const hex = as(j.skin);
    if (hex && palette.some((c) => c.hex === hex)) jobs[j.id] = `acc-${hex}`;
  }
  /* `fromNames`: her palette had no codes, so these were matched to her
     colour names (colourNames.js) — Me says so before she uses them. */
  return { palette, jobs, fromNames: !!fromNames };
}

/**
 * THE ONE READER. The skin every tool wears: Me's jobs, then the
 * Accelerator's for any job Me leaves empty. Adds what only Me can say — the
 * words that go on each button (`buttonText`, `button2Text`) — and `palette`,
 * every colour she named, for the tools that can use more than the jobs.
 */
export function brandSkin({ brand, voiceOutputs = {} } = {}) {
  const read = readBrandSkin({
    palette: voiceOutputs?.["Brand Feeling & Color Palette"] || voiceOutputs?.["Color Palette"] || "",
    typography: voiceOutputs?.["Typography Pairing"] || "",
  });
  const b = readBrand(brand);
  const { jobs } = effectiveJobs(brand);
  const skin = { ...read.skin };
  const fromMe = [];
  for (const j of BRAND_JOBS) {
    const c = b.palette.find((x) => x.id === jobs[j.id]);
    if (!c) continue;
    /* "#RRGGBB" — the form readBrandSkin gives, so the tools see one form. */
    skin[j.skin] = `#${c.hex}`;
    fromMe.push(j.skin);
    if (j.id === "button") skin.buttonText = `#${onOf(c)}`;
    if (j.id === "button2") skin.button2Text = `#${onOf(c)}`;
  }
  if (b.palette.length) skin.palette = b.palette.map((c) => ({ name: c.name, hex: `#${c.hex}` }));
  /* Her fonts beat the Accelerator's, slot by slot. */
  for (const s of FONT_SLOTS) if (b.fonts[s.id]) { skin[s.skin] = b.fonts[s.id]; fromMe.push(s.skin); }
  return { skin, read, fromMe };
}

/**
 * Where Me and the Accelerator DISAGREE — only jobs both have filled, with
 * different colours. An empty side is a gap, not a conflict.
 * Returns [{ job, label, mine: {name, hex}, accelerator: {name, hex} }].
 */
export function brandConflict(brand, voiceOutputs = {}) {
  const theirs = acceleratorBrand(voiceOutputs);
  return BRAND_JOBS.flatMap((j) => {
    const m = jobColour(brand, j.id);
    /* Only what the Accelerator actually SAID — a job worked out from roles
       on its side is not a decision it made, so it cannot disagree with her
       (her real account showed "Links differ" when the prose never names a
       links color). */
    const t = theirs.palette.find((c) => c.id === theirs.jobs[j.id]);
    return m && t && m.hex !== t.hex ? [{ job: j.id, label: j.label, mine: { name: m.name, hex: m.hex }, accelerator: { name: t.name, hex: t.hex } }] : [];
  });
}

/**
 * Each filled job's contrast against what it sits on: { ratio, ok } or null
 * where the job has nothing to be read against. A note, never a gate: her
 * brand is hers.
 */
export function jobContrast(brand, jobId) {
  const job = BRAND_JOBS.find((j) => j.id === jobId);
  const c = jobColour(brand, jobId);
  if (!job || !c || !job.against) return null;
  const other = job.against === "own" ? onOf(c) : jobColour(brand, job.against)?.hex;
  const r = ratio(c.hex, other);
  return r === null ? null : { ratio: r, ok: r >= job.min, against: job.against };
}

/**
 * AFTER A LOCK in My Voice & Branding (her rule, 2026-10-12: "it should say,
 * do you want me to update your brand colors in Me"). Given the section she
 * just locked and what Me holds now, the question to ask — or null when there
 * is nothing to ask (the lock matches Me, or holds nothing readable).
 *   mode "fill"   — Me has none yet: "Add these to Me → Brand?"
 *   mode "differ" — Me has different ones: "Update Me to these?"
 * `next` is the whole brand to save if she says yes; nothing is saved here.
 */
export function brandAfterLock(sectionName, content, currentBrand) {
  const mine = readBrand(currentBrand);
  if (sectionName === "Brand Feeling & Color Palette") {
    const voice = { "Brand Feeling & Color Palette": content };
    const theirs = acceleratorBrand(voice);
    if (!theirs.palette.length) return null;
    const mode = mine.palette.length === 0 ? "fill" : brandConflict(currentBrand, voice).length ? "differ" : null;
    if (!mode) return null;
    return { kind: "colors", mode, colors: theirs.palette.map((c) => ({ name: c.name, hex: c.hex })), next: { ...mine, palette: theirs.palette, jobs: theirs.jobs } };
  }
  if (sectionName === "Typography Pairing") {
    const voice = { "Typography Pairing": content };
    const theirs = acceleratorFonts(voice);
    if (!theirs.heading && !theirs.body) return null;
    const mode = !mine.fonts.heading && !mine.fonts.body ? "fill" : fontConflict(currentBrand, voice).length ? "differ" : null;
    if (!mode) return null;
    return { kind: "fonts", mode, fonts: theirs, next: { ...mine, fonts: { ...mine.fonts, ...theirs } } };
  }
  return null;
}
